// src/components/admin/page-sections/SlideshowLinearSection.js
import React, { useState, useEffect, useCallback } from 'react';

const SlideshowLinearSection = ({ content }) => {
  const {
    slides = [
      {
        imageUrl: '/images/s6.jpg',
        title: 'Transforming Packaging, Elevating Brands',
        subtitle: ''
      },
      {
        imageUrl: '/images/s7.jpg',
        title: 'Transforming Packaging, Elevating Brands',
        subtitle: ''
      }
    ],
    height = '900px',
    overlayOpacity = 40,
    textColor = '#ffffff',
    transitionSpeed = 5000,
    animationStyle = 'fade',
    showArrows = true,
    showIndicators = true,
    textAlignment = 'center'
  } = content;

  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    const slideInterval = setInterval(nextSlide, transitionSpeed);
    return () => clearInterval(slideInterval);
  }, [nextSlide, transitionSpeed]);

  const alignmentClasses = {
    left: 'justify-start text-left',
    center: 'justify-center text-center',
    right: 'justify-end text-right'
  };

  const animationClasses = {
    fade: 'transition-opacity duration-1000',
    slide: 'transition-transform duration-1000',
    zoom: 'transition-all duration-1000'
  };

  return (
    <div 
      className="relative w-full overflow-hidden group" 
      style={{ height }}
    >
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 ${animationClasses[animationStyle]} ${
            index === currentSlide 
              ? 'opacity-100 scale-100 translate-x-0' 
              : animationStyle === 'fade' 
                ? 'opacity-0 scale-100 translate-x-0' 
                : animationStyle === 'slide' 
                  ? `opacity-100 translate-x-${index < currentSlide ? '-' : ''}full` 
                  : 'opacity-0 scale-110 translate-x-0'
          }`}
        >
          <div 
            className="absolute inset-0 bg-black" 
            style={{ opacity: overlayOpacity / 100 }}
          ></div>
          <img 
            src={slide.imageUrl} 
            alt={slide.title || `Slide ${index + 1}`} 
            className="w-full h-full object-cover"
            loading={index === 0 ? "eager" : "lazy"}
          />
          <div className={`absolute inset-0 flex items-center ${alignmentClasses[textAlignment]}`}>
            <div className="px-4 max-w-5xl mx-auto">
              {slide.title && (
                <h2 
                  className="text-4xl md:text-5xl font-bold drop-shadow-lg mb-4" 
                  style={{ color: textColor }}
                >
                  {slide.title}
                </h2>
              )}
              {slide.subtitle && (
                <p 
                  className="text-xl md:text-2xl drop-shadow-lg" 
                  style={{ color: textColor }}
                >
                  {slide.subtitle}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
      
      {showArrows && slides.length > 1 && (
        <>
          <button 
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-3 rounded-full transition-all opacity-0 group-hover:opacity-100"
            aria-label="Previous Slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button 
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 text-white p-3 rounded-full transition-all opacity-0 group-hover:opacity-100"
            aria-label="Next Slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {showIndicators && slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-white w-6' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SlideshowLinearSection;